import React, { forwardRef } from "react"
import styled from "styled-components"
import PropTypes from "prop-types"

const colorDefault = "blue"
const color = {
  // color: ["left", "right", "shadow"]
  white: ["#FFF", "#FFF", "rgba(0, 0, 0, .15)"],
  gray: ["#96A2B0", "#697786", "rgba(105, 119, 134, .20)"],
  dark: ["#292F34", "#292F34", "rgba(41, 47, 52, .20)"],
  blue: ["#5ebefa", "#3089f3", "rgba(35, 123, 245, .39)"],
  newBlue: ["#2D54F1", "#3089f3", "rgba(35, 123, 245, .39)"],
  green: ["#71D77C", "#5AC866", "rgba(98, 211, 109, .39)"],
  red: ["#FF5C5C", "#ed4949", "rgba(222, 62, 41, .33)"],
  facebook: ["#3b5998", "#8b9dc3", "rgba(139, 157, 195, .39)"],
  google: ["#4285F4", "#7BAAF7", "rgba(66, 133, 244, .39)"],
  disabled: ["#adb5bd", "#6c757d", "rgba(173, 181,189, .39)"],
}

const getColor = (props, whichOne) => {
  const side = { left: 0, right: 1, shadow: 2 }
  if (props.disabled) props.color = "disabled"
  return props.$color
    ? color[props.$color][side[whichOne]]
    : color[colorDefault][side[whichOne]]
}

const getRadius = (height, shape) => {
  const heightNum = height * 0.07
  switch (shape) {
    case "round":
      return height
    case "roundSquare":
      return 12
    case "square":
    default:
      return heightNum
  }
}

// const WinItButtonBase = as => styled[as]`
const WinItButtonBase = styled.button`
  text-decoration: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-appearance: none;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: ${(props) => getRadius(props.$height, props.$shape)}px;
  height: ${(props) => props.$height}px;
  ${(props) => (props.$width ? `width: ${props.$width};` : null)}
  ${(props) => (props.maxWidth ? `max-width: ${props.maxWidth};` : null)}
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  font-size: ${(props) => props.$fontSize};
  color: ${(props) => (props.$color === "white" ? "black" : "white")};
  border: none;
  outline: none;
  user-select: none;
  ${(props) => (props.$padding ? `padding: ${props.$padding};` : null)};
  background: linear-gradient(to bottom right,${(props) =>
    getColor(props, "left")} 0%,${(props) => getColor(props, "right")} 100%);

  box-shadow: ${(props) =>
    `inset 0px 0px 40px rgba(33, 33, 33, .0), ${
      props.color
        ? `0px 5px 10px 0px ${getColor(props, "shadow")}`
        : "0px 0px 0px 0px rgba(0,0,0,0)"
    }`};
  &:after {
    background: linear-gradient(
      135deg,
      ${(props) => getColor(props, "left")} 0%,
      ${(props) => getColor(props, "right")} 100%
    );
  }
  &:disabled {
    background: linear-gradient(
      135deg,
      ${() => getColor({ color: "disabled" }, "right")} 0%,
      ${() => getColor({ color: "disabled" }, "left")} 100%
    );
    box-shadow: none;
    cursor: default;
  }
  &:hover {
    box-shadow: ${(props) =>
      `inset 0px ${props.color ? "5px 10px" : "14px 24px"} ${getColor(
        props,
        "shadow"
      )}, 0px 0px 0px 0px rgba(0,0,0,0)`};
    background: ${(props) =>
      `linear-gradient(to bottom right,${getColor(
        props,
        "right"
      )} 0%,${getColor(props, "left")} 100%)`};
  }
  transition: all 0.5s;
`

const WinItButton = forwardRef((props_, ref) => {
  // Prevent these props from leaking into the DOM, keep onClick handlers and all other non-default props:
  const props = { ...props_ } // We can't delete directly off props, so take a shallow copy
  const { as, color: colorProp, padding, height, width, fontSize, disabled, shape } = props // Destructure defaults so they can be passed as transient props
  delete props.as
  delete props.color
  delete props.padding
  delete props.height
  delete props.width
  delete props.fontSize
  delete props.shape

  return (
    <WinItButtonBase
      {...props}
      $as={as}
      $color={colorProp}
      $padding={padding}
      $height={height}
      $width={width}
      $fontSize={fontSize}
      $disabled={disabled}
      $shape={shape}
      ref={ref}
    />
  )
})

WinItButton.propTypes = {
  as: PropTypes.oneOf(["a", "button"]),
  color: PropTypes.oneOf(Object.keys(color)),
  padding: PropTypes.string,
  height: PropTypes.number,
  width: PropTypes.string,
  fontSize: PropTypes.string,
  disabled: PropTypes.bool,
  shape: PropTypes.oneOf(["round", "roundSquare", "square"]),
}

WinItButton.defaultProps = {
  as: "button",
  color: colorDefault,
  shape: "square",
  padding: null,
  width: null,
  height: 64,
  fontSize: "20px",
  disabled: false,
}

export default WinItButton
